// #region Imports
import { useCallback, useEffect, useState } from "react"
import Page from "../components/Page"
import { allPermissions, hasPermission } from "../utils/permissions"
import useFetchWithMsal from "../hooks/useFetchWithMsal"

// MUI Imports
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Box,
  LinearProgress,
  MenuItem,
  Tooltip,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
// ICONS
import { Add } from "@mui/icons-material"
import SearchIcon from "@mui/icons-material/Search"
import DeleteIcon from "@mui/icons-material/Delete"
import TableRowsIcon from "@mui/icons-material/TableRows"
import ReorderIcon from "@mui/icons-material/Reorder"
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify"

// #endregion

export function MasterDataManager() {
  // #region State and Context
  const [dataTable, setDataTable] = useState("")
  const [getEndpoint, setGetEndpoint] = useState(null)
  const [putEndpoint, setPutEndpoint] = useState(null)
  const [postEndpoint, setPostEndpoint] = useState(null)
  const [deleteEndpoint, setDeleteEndpoint] = useState(null)
  const [processedData, setProcessedData] = useState([])
  const [rowView, setRowView] = useState("comfortable") // Enum? comfortable, standard, compact
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [rowData, setRowData] = useState(null)
  const [bodyFormat, setBodyFormat] = useState({})
  const [selectedRows, setSelectedRows] = useState([])

  const canEdit = !(
    hasPermission(allPermissions.MASTERDATA_WRITER) ||
    hasPermission(allPermissions.ADMIN)
  )

  // #endregion

  // #region custom useFetchWithMsal hook calls
  const {
    execute: excuteGetMasterDataMap,
    data: map,
    isLoading,
  } = useFetchWithMsal()

  const {
    execute: excuteGetEndpoints,
    data: endpoints,
    isLoading: isLoadingEndpoints,
  } = useFetchWithMsal()

  const {
    execute: excuteGetMasterData,
    data,
    isLoading: isLoadingData,
  } = useFetchWithMsal()

  const { execute: excutePostMasterData, isLoading: isPosting } =
    useFetchWithMsal()
  const { execute: excutePutMasterData, isLoading: isUpdating } =
    useFetchWithMsal()
  const { execute: excuteDeleteMasterData, isloading: isDeleting } =
    useFetchWithMsal()
  // #endregion

  // #region Data Processing and API Calls

  // Retrieve the master data using the provided API
  //useFetchWithMsal always appends local url as base URL - need to change this to use the provided API base URL
  const CallMasterDataApi = useCallback(
    (apiData, data = {}, params = "") => {
      switch (apiData.method) {
        case "GET":
          excuteGetMasterData(`${apiData.method}`, `/${apiData.endpoint}`)
          break
        case "POST":
          excutePostMasterData(
            `${apiData.method}`,
            `/${apiData.endpoint}`,
            data
          )
          break
        case "PUT":
          excutePutMasterData(
            `${apiData.method}`,
            `/${apiData.endpoint}${params}`,
            data
          )
          break
        case "DELETE":
          excuteDeleteMasterData(
            `${apiData.method}`,
            `/${apiData.endpoint}${params}`
          )
          break
        default:
          console.alert("Unsupported method:", apiData.method)
          break
      }
    },
    [
      excuteGetMasterData,
      excutePostMasterData,
      excutePutMasterData,
      excuteDeleteMasterData,
    ]
  )

  // Retrieve the master data tables on initial load
  useEffect(() => {
    if (!map && !isLoading) {
      excuteGetMasterDataMap("GET", "/api/masterdata")
    }
  }, [excuteGetMasterDataMap, map, isLoading])

  // Retrieve the endpoints for the selected master data table
  useEffect(() => {
    if (dataTable && Array.isArray(endpoints)) {
      setGetEndpoint(endpoints.find((ep) => ep.method === "GET") || null)
      setPutEndpoint(endpoints.find((ep) => ep.method === "PUT") || null)
      setPostEndpoint(endpoints.find((ep) => ep.method === "POST") || null)
      setDeleteEndpoint(endpoints.find((ep) => ep.method === "DELETE") || null)
    }
  }, [dataTable, endpoints])

  useEffect(() => {
    if (getEndpoint) {
      // Call the API to get the data for the selected master data table
      CallMasterDataApi(getEndpoint)
    }
  }, [getEndpoint, CallMasterDataApi])

  // Potentially should be moved to a separate hook or utility function that can accept prefered keys instead of just using an array index
  // Process the data to ensure each item has a unique ID for DataGrid
  useEffect(() => {
    if (data) {
      setProcessedData(
        data?.map((item, index) => {
          item.portUuid = index // Ensure each item has a unique ID for DataGrid
          return item
        })
      )
    }
  }, [data])

  // #endregion

  // #region UI Handlers
  const OnChangeRowView = (viewMode) => {
    setRowView(viewMode)
  }

  const OnOpenEditModal = (row) => {
    setEditModalOpen(row)
  }

  const OnAddNewRecord = (body) => {
    if (postEndpoint && canEdit) {
      // Here you would typically open a dialog or form to add a new record
      // For now, we just log the action
      console.log("Add new record clicked with body:", body)
      CallMasterDataApi(postEndpoint, body)
    } else {
      console.alert("No POST endpoint available for adding new records.")
    }
  }

  const OnDeleteSelected = () => {
    if (deleteEndpoint && selectedRows.length > 0 && canEdit) {
      // Build search params string from deleteEndpoint.searchParams and selectedRows
      const searchParams = (JSON.parse(deleteEndpoint?.searchParams) || [])
        .map((param) => {
          const encodedValues = selectedRows
            .map((row) => encodeURIComponent(row[param] ?? ""))
            .join(",")
          return `?${param}=${encodedValues}`
        })
        .join("&")
      CallMasterDataApi(deleteEndpoint, {}, searchParams)
      setSelectedRows([]) // Clear selected rows after deletion
      refreshData()
    } else {
      console.alert("No DELETE endpoint available or no rows selected.")
    }
  }

  const OnEditRecord = (body) => {
    if (putEndpoint && canEdit) {
      // Build search params string from putEndpoint.searchParams and rowData
      const searchParams =
        "/" +
        (JSON.parse(putEndpoint?.searchParams) || [])
          .map((param) => `${encodeURIComponent(rowData[param] ?? "")}`)
          .join("&")
      CallMasterDataApi(putEndpoint, body, searchParams)
    } else {
      console.alert("No PUT endpoint available for editing records.")
    }
  }

  const OnSearch = (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      setProcessedData(data)
      return
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase()
    const filteredData = data.filter((item) =>
      Object.values(item).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(lowerCaseSearchTerm)
      )
    )

    setProcessedData(filteredData)
  }

  // Handle the change of selected master data table
  const onTableChange = async (key) => {
    setDataTable(key)

    await excuteGetEndpoints("GET", `/api/masterdata/?key=${key}`)
  }

  const refreshData = () => {
    if (getEndpoint) {
      CallMasterDataApi(getEndpoint)
    }
  }
  // #endregion

  // #region Returned JSX
  return (
    <Page title="Master Data Management" fullHeight>
      {(isLoading ||
        isLoadingEndpoints ||
        isLoadingData ||
        isPosting ||
        isUpdating ||
        isDeleting) && (
        <LinearProgress
          style={{
            position: "absolute",
            width: "100%",
            top: 0,
            left: 0,
            zIndex: 1000,
          }}
        />
      )}
      {Array.isArray(map) && map.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Header Section / Toolbar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 1,
              mt: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            {/* Table Selector */}
            <TextField
              sx={{ minWidth: 300, ml: 1 }}
              size="small"
              fullWidth={false}
              label="Table Selected"
              variant="outlined"
              select
              value={dataTable}
              onChange={(e) => onTableChange(e.target.value)}
            >
              {map?.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.description}
                </MenuItem>
              ))}
            </TextField>

            {/* Search Bar */}
            <TextField
              size="small"
              sx={{ flexGrow: 1, minWidth: 300, maxWidth: 700 }}
              fullWidth={true}
              label="Search"
              variant="outlined"
              placeholder="Search for a record"
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
              onChange={(e) => OnSearch(e.target.value)}
            />

            {/* Action Buttons */}
            <Tooltip
              title={
                postEndpoint === null
                  ? "No endpoint available for saving."
                  : !canEdit
                  ? "You do not have permission to edit."
                  : ""
              }
            >
              <span style={{ display: "inline-block" }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => {
                    OnOpenEditModal(true)
                    setBodyFormat(
                      JSON.parse(postEndpoint?.body)?.reduce((acc, key) => {
                        acc[key] = ""
                        return acc
                      }, {})
                    )
                  }}
                  disabled={putEndpoint === null || !canEdit}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                deleteEndpoint === null
                  ? "No endpoint available for deleting."
                  : !canEdit
                  ? "You do not have permission to edit."
                  : selectedRows.length === 0
                  ? "No rows selected for deletion."
                  : ""
              }
            >
              <span style={{ display: "inline-block" }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={
                    deleteEndpoint === null ||
                    selectedRows.length === 0 ||
                    !canEdit
                  }
                  onClick={OnDeleteSelected}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>

            {/* View mode buttons grouped */}
            <Box
              sx={{
                alignItems: "center",
                padding: "2px 4px",
                gap: 1,
                height: 40,
                mr: 1,
              }}
            >
              <Tooltip title="Row View">
                <IconButton
                  size="medium"
                  sx={{ width: 36, height: 36 }}
                  onClick={() => OnChangeRowView("comfortable")}
                >
                  <TableRowsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compact View">
                <IconButton
                  size="medium"
                  sx={{ width: 36, height: 36 }}
                  onClick={() => OnChangeRowView("standard")}
                >
                  <ReorderIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  size="medium"
                  sx={{ width: 36, height: 36 }}
                  onClick={() => OnChangeRowView("compact")}
                >
                  <FormatAlignJustifyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {/* Data Table Section */}
          <Box
            sx={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            {dataTable && data && data.length > 0 ? (
              <DataGrid
                columns={[
                  ...Object.keys(data[0]).map((key) => ({
                    field: key,
                    headerName: key.charAt(0).toUpperCase() + key.slice(1),
                    flex: 1,
                  })),
                ]}
                rows={processedData || []}
                onRowDoubleClick={(row) => {
                  if (!putEndpoint) {
                    console.alert(
                      "No PUT endpoint available for editing records."
                    )
                    return
                  }
                  setRowData(row.row)
                  OnOpenEditModal(true)
                  setBodyFormat(
                    JSON.parse(putEndpoint?.body)?.reduce((acc, key) => {
                      acc[key] = row.row[key] || ""
                      return acc
                    }, {})
                  )
                }}
                onRowSelectionModelChange={(selectedRows) => {
                  setSelectedRows(
                    selectedRows.map((rowId) =>
                      processedData.find((row) => {
                        if (row.portUuid === rowId) {
                          return row
                        }
                        return null
                      })
                    )
                  )
                }}
                loading={isLoadingData}
                getRowId={(row) => row.portUuid}
                autoHeight={false}
                checkboxSelection={true}
                density={rowView}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  border: 0,
                  padding: 5,
                }}
                disableRowSelectionOnClick
              />
            ) : (
              <Box
                sx={{
                  minHeight: 350,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Typography variant="h6" color="textSecondary">
                  No master data table selected.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <LinearProgress
          style={{
            position: "absolute",
            width: "100%",
            top: 0,
            left: 0,
            zIndex: 1000,
          }}
        />
      )}

      {/* Edit Modal */}
      <Dialog
        open={Boolean(editModalOpen)}
        onClose={() => {
          setEditModalOpen(false)
          setRowData(null)
        }}
        maxWidth="md"
        fullWidth
      >
        {/* add some debouncing on the inputs? */}
        <Box sx={{ padding: 2, display: "flex", flexDirection: "column" }}>
          <Box sx={{ pb: 1, textAlign: "center", textEmphasis: "bold" }}>
            {rowData ? (
              <DialogTitle>Edit Record</DialogTitle>
            ) : (
              <DialogTitle>Add New Record</DialogTitle>
            )}
          </Box>

          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {Object.keys(bodyFormat || {}).map((key) => (
                <TextField
                  key={key}
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  value={bodyFormat[key] || ""}
                  onChange={(e) =>
                    setBodyFormat({
                      ...bodyFormat,
                      [key]: e.target.value,
                    })
                  }
                  fullWidth
                />
              ))}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setEditModalOpen(false)
                setRowData(null)
              }}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (rowData) {
                  // If rowData exists, we are editing an existing record
                  OnEditRecord(bodyFormat)
                } else {
                  // If rowData does not exist, we are adding a new record
                  OnAddNewRecord(bodyFormat)
                }
                setEditModalOpen(false)
                setRowData(null)
                setBodyFormat({})
                refreshData()
              }}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Page>
  )

  // #endregion
}

export default MasterDataManager
